import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/useAuthStore';
import { firestore, collection, getDocs, query, where, doc, getDoc } from '../lib/firebase';
import { User, Trophy, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { validateUsername } from '../utils/validateUsername';
import AvatarPicker, { getAvatarById } from './AvatarPicker';
import { ProfileSkeleton } from './Skeleton';

export { getAvatarById };

interface GameStats {
  total_games: number;
  wins: number;
  draws: number;
  losses: number;
}

export default function UserProfile() {
  const navigate = useNavigate();
  const { user, profile, updateProfile } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [username, setUsername] = useState(profile?.username || '');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [gameStats, setGameStats] = useState<GameStats>({
    total_games: 0,
    wins: 0,
    draws: 0,
    losses: 0
  });
  const [friends, setFriends] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    if (profile) {
      setUsername(profile.username);
      fetchGameStats();
      fetchFriends();
    }
  }, [user, profile]);

  const fetchGameStats = async () => {
    if (!user) return;
    try {
      const snap = await getDocs(collection(firestore, 'gameResults'));
      const data = snap.docs
        .map(d => d.data())
        .filter(r => r.player1_id === user.uid || r.player2_id === user.uid);

      setGameStats({
        total_games: data.length,
        wins: data.filter(game => game.winner_id === user.uid).length,
        draws: data.filter(game => game.is_draw).length,
        losses: data.filter(game => game.winner_id && game.winner_id !== user.uid).length
      });
    } catch (error) {
      console.error('Error fetching game stats:', error);
    }
  };

  const fetchFriends = async () => {
    if (!user) return;
    try {
      const q = query(
        collection(firestore, 'friendships'),
        where('user_id', '==', user.uid),
        where('status', '==', 'accepted')
      );
      const snap = await getDocs(q);
      const friendsList: any[] = [];
      for (const friendDoc of snap.docs) {
        const friendId = friendDoc.data().friend_id;
        const profileSnap = await getDoc(doc(firestore, 'profiles', friendId));
        if (profileSnap.exists()) {
          friendsList.push({ profiles: profileSnap.data() });
        }
      }
      setFriends(friendsList);
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  };

  const handleSave = async () => {
    const error = validateUsername(username);
    if (error) {
      setUsernameError(error);
      return;
    }

    setLoading(true);
    try {
      await updateProfile({ username: username.trim() });
      setIsEditing(false);
      setUsernameError(null);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarSelect = async (avatarId: string) => {
    try {
      await updateProfile({ profilePicture: avatarId });
      setShowAvatarPicker(false);
    } catch (error) {
      console.error('Error updating avatar:', error);
    }
  };

  if (!user || !profile) {
    return <ProfileSkeleton />;
  }

  const currentAvatar = getAvatarById(user.photoURL);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Profile Card */}
      <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl p-6 border border-white/10">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <button
            onClick={() => setShowAvatarPicker(!showAvatarPicker)}
            className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-600 to-violet-600 flex items-center justify-center text-4xl hover:ring-2 hover:ring-pink-400 transition shrink-0"
          >
            {currentAvatar ? currentAvatar.emoji : <User className="w-12 h-12 text-white" />}
          </button>

          {/* Info */}
          <div className="flex-1">
            {isEditing ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setUsernameError(null); }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                  {usernameError && <p className="mt-1 text-sm text-red-400">{usernameError}</p>}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => { setIsEditing(false); setUsername(profile.username); setUsernameError(null); }}
                    className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold">{profile.username}</h1>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-1 bg-white/10 text-sm rounded-lg hover:bg-white/20 transition"
                  >
                    Edit
                  </button>
                </div>
                <p className="text-gray-300 text-sm">
                  Joined {new Date(profile.created_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Avatar Picker */}
        {showAvatarPicker && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <h3 className="text-sm font-medium text-gray-300 mb-3">Choose your avatar</h3>
            <AvatarPicker selectedId={user.photoURL} onSelect={handleAvatarSelect} />
          </div>
        )}
      </div>

      {/* Stats & Friends */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Game Statistics
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-pink-500">{gameStats.total_games}</div>
              <div className="text-sm text-gray-300">Total Games</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-500">{gameStats.wins}</div>
              <div className="text-sm text-gray-300">Wins</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-500">{gameStats.draws}</div>
              <div className="text-sm text-gray-300">Draws</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-500">{gameStats.losses}</div>
              <div className="text-sm text-gray-300">Losses</div>
            </div>
          </div>
          {gameStats.total_games > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-sm text-gray-300">Win Rate</div>
              <div className="text-lg font-semibold text-pink-500">
                {Math.round((gameStats.wins / gameStats.total_games) * 100)}%
              </div>
            </div>
          )}
        </div>

        <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Friends ({friends.length})
          </h2>
          {friends.length > 0 ? (
            <div className="space-y-2">
              {friends.slice(0, 5).map((friendship, index) => (
                <div key={index} className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                  <div className="w-8 h-8 bg-gradient-to-br from-pink-600 to-violet-600 rounded-full flex items-center justify-center">
                    {friendship.profiles?.avatar_url
                      ? <span className="text-lg">{getAvatarById(friendship.profiles.avatar_url)?.emoji || '?'}</span>
                      : <User className="w-4 h-4 text-white" />}
                  </div>
                  <span className="text-sm">{friendship.profiles?.username}</span>
                </div>
              ))}
              {friends.length > 5 && (
                <div className="text-sm text-gray-300 text-center pt-2">
                  And {friends.length - 5} more...
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-300 py-8">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No friends yet</p>
              <p className="text-sm">Start playing games to meet new people!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
